
import React, { useMemo, useState } from 'react';
import BoardLayout from '../components/kanban/components/BoardLayout';
import { Card, Typography, Row, Col, Space, Tag, Tooltip } from 'antd';
import ReactApexChart from 'react-apexcharts';
import { DEFAULT_USERS } from '../components/kanban/constants';

const users = DEFAULT_USERS;

const Analytics = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterPriority, setFilterPriority] = useState('');

  const tasks = [];
  const filteredTasks = tasks;

  const boardStats = useMemo(() => ({
    totalTasks: 0,
    completedTasks: 0,
    inProgressTasks: 0,
    highPriorityTasks: 0,
    overdueTasks: 0,
    completionRate: 0,
  }), []);

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilterAssignee('');
    setFilterPriority('');
  };

  const handleAddTask = () => {};
  const handleExportData = () => {};

  // State for selected day and task details
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayTasks, setDayTasks] = useState({});

  // Calendar configuration with random active days and tasks
  const currentMonthCalendar = useMemo(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    
    // Get first and last day of current month
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Generate random active days with task counts (approximately 40% of days)
    const activeDaysWithTasks = {};
    const activeDaysCount = Math.floor(daysInMonth * 0.4);
    
    for (let i = 0; i < activeDaysCount; i++) {
      const randomDay = Math.floor(Math.random() * daysInMonth) + 1;
      const taskCount = Math.floor(Math.random() * 8) + 1; // 1-8 tasks
      activeDaysWithTasks[randomDay] = taskCount;
    }
    
    // Set initial day tasks
    setDayTasks(activeDaysWithTasks);
    
    // Create calendar structure
    const weeks = [];
    let currentWeek = [];
    
    // Add empty cells for days before the first day of month
    const firstDayOfWeek = firstDay.getDay();
    for (let i = 0; i < firstDayOfWeek; i++) {
      currentWeek.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      currentWeek.push({
        day,
        isActive: day in activeDaysWithTasks,
        isToday: day === today.getDate(),
        taskCount: activeDaysWithTasks[day] || 0
      });
      
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }
    
    // Add empty cells for remaining days in last week
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }
    
    return weeks;
  }, []);

  const handleDayClick = (dayInfo) => {
    if (dayInfo) {
      setSelectedDay(dayInfo);
    }
  };

  // O&M Costs Bar Chart for 12 months - Improved version
  const omCostsBarChart = useMemo(() => ({
    options: {
      chart: {
        type: 'bar',
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      plotOptions: {
        bar: {
          columnWidth: '55%',
          borderRadius: 4,
          dataLabels: {
            position: 'top',
          }
        }
      },
      dataLabels: {
        enabled: true,
        offsetY: -25,
        style: {
          fontSize: '11px',
          fontWeight: 'bold',
          colors: ['#304758']
        },
        formatter: function(val) {
          // Format numbers to K for thousands to save space
          if (val >= 1000) {
            return '$' + (val / 1000).toFixed(1) + 'K';
          }
          return '$' + val;
        }
      },
      xaxis: {
        categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        },
        labels: {
          style: {
            colors: '#666',
            fontSize: '11px'
          },
          rotate: -45
        }
      },
      yaxis: {
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        },
        labels: {
          show: true,
          formatter: function(val) {
            if (val >= 1000) {
              return '$' + (val / 1000).toFixed(0) + 'K';
            }
            return '$' + val;
          },
          style: {
            colors: '#666',
            fontSize: '11px'
          }
        },
        max: 20000, // Set max to create more space for data labels
        tickAmount: 5
      },
      grid: {
        strokeDashArray: 3,
        borderColor: '#f0f0f0',
        padding: {
          top: 20,
          right: 10,
          bottom: 0,
          left: 10
        }
      },
      colors: ['#1890ff'],
      title: {
        text: 'Total O&M Costs by Month',
        align: 'center',
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#333'
        }
      },
      tooltip: {
        y: {
          formatter: function(val) {
            return '$' + val.toLocaleString();
          }
        }
      },
      // Add more spacing for data labels
      annotations: {
        yaxis: [{
          y: 20000,
          borderColor: '#fff',
          label: {
            borderColor: '#fff',
            style: {
              color: '#fff',
              background: '#fff',
            },
            text: ''
          }
        }]
      }
    },
    series: [{
      name: 'O&M Costs',
      data: [12500, 13200, 14100, 15800, 14300, 17500, 16200, 14800, 13900, 15100, 16800, 18200]
    }]
  }), []);

  // Equipment Uptime Chart
  const equipmentUptimeChart = useMemo(() => ({
    options: {
      chart: {
        type: 'line',
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      stroke: {
        curve: 'smooth',
        width: 3
      },
      markers: {
        size: 5
      },
      dataLabels: {
        enabled: false
      },
      xaxis: {
        categories: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        labels: {
          style: {
            colors: '#666',
            fontSize: '11px'
          }
        }
      },
      yaxis: {
        min: 85,
        max: 100,
        labels: {
          formatter: (val) => `${val}%`,
          style: {
            colors: '#666',
            fontSize: '11px'
          }
        },
        title: {
          text: 'Uptime Percentage',
          style: {
            fontSize: '12px',
            color: '#666'
          }
        }
      },
      grid: {
        strokeDashArray: 4,
        borderColor: '#f0f0f0'
      },
      colors: ['#52c41a'],
      title: {
        text: 'Equipment Uptime - Weekly Performance',
        align: 'center',
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#333'
        }
      },
      annotations: {
        yaxis: [
          {
            y: 95,
            borderColor: '#52c41a',
            label: {
              borderColor: '#52c41a',
              style: {
                color: '#fff',
                background: '#52c41a'
              },
              text: 'Target: 95%'
            }
          }
        ]
      },
      tooltip: {
        y: {
          formatter: (val) => `${val}% Uptime`
        }
      }
    },
    series: [
      {
        name: 'Uptime %',
        data: [96.2, 97.8, 95.4, 98.1, 96.7, 94.9, 97.3]
      }
    ]
  }), []);

  const operationTypesChart = useMemo(() => ({
    options: {
      chart: {
        type: 'bar',
        toolbar: { show: false },
        stacked: true
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 4
        }
      },
      dataLabels: {
        enabled: false
      },
      xaxis: {
        categories: ['Electrical', 'Mechanical', 'HVAC', 'Plumbing', 'Safety', 'Inspection'],
        labels: {
          style: {
            colors: '#666',
            fontSize: '11px'
          }
        }
      },
      yaxis: {
        title: {
          text: 'Number of Operations',
          style: {
            fontSize: '12px',
            color: '#666'
          }
        },
        labels: {
          style: {
            colors: '#666',
            fontSize: '11px'
          }
        }
      },
      grid: {
        strokeDashArray: 4,
        borderColor: '#f0f0f0'
      },
      colors: ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'],
      title: {
        text: 'Operations by Type & Status',
        align: 'center',
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#333'
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'center'
      }
    },
    series: [
      {
        name: 'Completed',
        data: [45, 38, 52, 28, 41, 35]
      },
      {
        name: 'In Progress',
        data: [12, 15, 8, 10, 7, 9]
      },
      {
        name: 'Pending',
        data: [5, 8, 6, 12, 4, 7]
      }
    ]
  }), []);

  return (
    <BoardLayout
      sidebarCollapsed={sidebarCollapsed}
      setSidebarCollapsed={setSidebarCollapsed}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      filterAssignee={filterAssignee}
      setFilterAssignee={setFilterAssignee}
      filterPriority={filterPriority}
      setFilterPriority={setFilterPriority}
      boardStats={boardStats}
      filteredTasks={filteredTasks}
      tasks={tasks}
      handleClearSearch={handleClearSearch}
      handleAddTask={handleAddTask}
      handleExportData={handleExportData}
      users={users}
      showControls={false}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>Analytics & KPI Dashboard</Typography.Title>

        {/* KPI cards */}
        <Row gutter={[16, 16]}>
          {[
            { title: 'AVG COST PER OPERATION', value: '$245' },
            { title: 'AVG TIME PER OPERATION', value: '2.3h' },
            { title: 'AVAILABLE TECHNICIANS', value: '12' },
            { title: 'OPERATIONS LAST MONTH', value: '156' }
          ].map((kpi) => (
            <Col xs={24} sm={12} md={12} lg={6} key={kpi.title}>
              <Card
                style={{ borderRadius: 12, border: '1px solid #e8e8e8' }}
                bodyStyle={{ padding: 16 }}
              >
                <div style={{ fontSize: 12, color: '#595959', marginBottom: 8 }}>{kpi.title}</div>
                <div style={{ fontSize: 36, fontWeight: 700, letterSpacing: 1 }}>{kpi.value}</div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Metrics section */}
        <Card
          title={<span style={{ fontWeight: 600 }}>METRICS</span>}
          style={{ borderRadius: 12, border: '1px solid #e8e8e8' }}
          bodyStyle={{ padding: 16 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: 16 }}>
                <Typography.Title level={5} style={{ marginBottom: 16, textAlign: 'center' }}>
                  Technician Activity Calendar - {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
                </Typography.Title>
                
                {/* Selected Day Info */}
                {selectedDay && (
                  <div style={{ 
                    marginBottom: 16, 
                    padding: 12, 
                    backgroundColor: '#f6ffed', 
                    border: '1px solid #b7eb8f',
                    borderRadius: 6,
                    textAlign: 'center'
                  }}>
                    <Typography.Text strong>
                      {selectedDay.day} {new Date().toLocaleString('default', { month: 'long' })}: 
                      <span style={{ color: '#389e0d', marginLeft: 8 }}>
                        {selectedDay.taskCount} task{selectedDay.taskCount !== 1 ? 's' : ''} completed
                      </span>
                    </Typography.Text>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 12, color: '#666' }}>
                      {day}
                    </div>
                  ))}
                  {currentMonthCalendar.flat().map((dayInfo, index) => (
                    <Tooltip 
                      key={index} 
                      title={dayInfo ? `${dayInfo.day} ${new Date().toLocaleString('default', { month: 'long' })}: ${dayInfo.taskCount} tasks` : ''}
                    >
                      <div
                        onClick={() => handleDayClick(dayInfo)}
                        style={{
                          height: 40,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: dayInfo?.isToday ? 'bold' : 'normal',
                          backgroundColor: dayInfo?.isActive ? 
                            (dayInfo?.isToday ? '#e6f7ff' : '#f6ffed') : 
                            (dayInfo?.isToday ? '#e6f7ff' : 'transparent'),
                          color: dayInfo?.isActive ? '#389e0d' : '#000',
                          border: dayInfo?.isToday ? '2px solid #1890ff' : 
                                 (dayInfo?.isActive ? '1px solid #b7eb8f' : '1px solid #f0f0f0'),
                          cursor: dayInfo ? 'pointer' : 'default',
                          transition: 'all 0.2s',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          if (dayInfo) e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          if (dayInfo) e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        {dayInfo ? (
                          <>
                            <div>{dayInfo.day}</div>
                            {dayInfo.isActive && dayInfo.taskCount > 0 && (
                              <div style={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                width: 16,
                                height: 16,
                                backgroundColor: '#52c41a',
                                borderRadius: '50%',
                                fontSize: 10,
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {dayInfo.taskCount}
                              </div>
                            )}
                          </>
                        ) : ''}
                      </div>
                    </Tooltip>
                  ))}
                </div>
                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 12, height: 12, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 2 }}></div>
                    <span style={{ fontSize: 12 }}>Active Day</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 12, height: 12, backgroundColor: '#e6f7ff', border: '2px solid #1890ff', borderRadius: 2 }}></div>
                    <span style={{ fontSize: 12 }}>Today</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 16, height: 16, backgroundColor: '#52c41a', borderRadius: '50%', fontSize: 10, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>3</div>
                    <span style={{ fontSize: 12 }}>Task Count</span>
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: 16 }}>
                <ReactApexChart 
                  options={omCostsBarChart.options} 
                  series={omCostsBarChart.series} 
                  type="bar" 
                  height={350} 
                />
              </div>
            </Col>
          </Row>
        </Card>

        {/* Operations Performance section */}
        <Card
          title={<span style={{ fontWeight: 600 }}>OPERATIONS PERFORMANCE</span>}
          style={{ borderRadius: 12, border: '1px solid #e8e8e8' }}
          bodyStyle={{ padding: 16 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: 16 }}>
                <ReactApexChart 
                  options={equipmentUptimeChart.options} 
                  series={equipmentUptimeChart.series} 
                  type="line" 
                  height={300} 
                />
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: 16 }}>
                <ReactApexChart 
                  options={operationTypesChart.options} 
                  series={operationTypesChart.series} 
                  type="bar" 
                  height={300} 
                />
              </div>
            </Col>
          </Row>
        </Card>
      </div>
    </BoardLayout>
  );
};

export default Analytics;
